/**
 * Global Properties Composable
 * Provides shared reactive state for components (header incident tabs, recording status)
 * Uses venueDataStore for incident data (updated via MQTT)
 */

import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import { useViewContextStore } from "@/stores/viewContext";
import { useVenueDataStore } from "@/stores/venueData";

export const useGlobalProperties = () => {
    const viewContext = useViewContextStore();
    const venueDataStore = useVenueDataStore();
    const { venueId } = storeToRefs(viewContext);

    // Active incidents for current venue (from venueDataStore, updated via MQTT)
    const activeIncidentList = computed(() => {
        if (!venueId.value) return { data: [] };

        const incidents = venueDataStore.getActiveIncidents(venueId.value);
        // Sort by severity (descending) - same as old query
        const sorted = [...incidents].sort((a, b) => {
            const severityOrder: Record<string, number> = {
                critical: 4,
                high: 3,
                medium: 2,
                low: 1,
            };
            const aSeverity = a.severity ? severityOrder[a.severity] ?? 0 : 0;
            const bSeverity = b.severity ? severityOrder[b.severity] ?? 0 : 0;
            return bSeverity - aSeverity;
        });

        return { data: sorted };
    });

    const isLoadingActiveIncidents = computed(() => {
        if (!venueId.value) return false;
        const venueData = venueDataStore.getVenueData(venueId.value);
        return venueData?.loading.incidents ?? false;
    });

    // Recording status based on active incidents
    const recordingStatus = computed(() => {
        if (isLoadingActiveIncidents.value) return false;
        return activeIncidentList.value.data.length > 0;
    });

    // Manual overrides (kept for backward compatibility)
    const manualRecordingOverride = ref(false);
    function recordingStart() {
        manualRecordingOverride.value = true;
    }
    function recordingStop() {
        manualRecordingOverride.value = false;
    }

    return {
        recordingStatus,
        isLoadingActiveIncidents,
        activeIncidentList,

        manualRecordingStart: recordingStart,
        manualRecordingStop: recordingStop,
        isManualRecordingOverrideActive: manualRecordingOverride,
    };
};

